import { Plus, Trash2, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {import('../api/meetings').MeetingGuest} MeetingGuest
 * @typedef {{
 *   id: string,
 *   firstName: string,
 *   lastName: string,
 *   phone: string,
 * }} GuestFormRow
 */

/**
 * @param {MeetingGuest[]} guests
 * @returns {GuestFormRow[]}
 */
function mapGuestsToRows(guests) {
    if (!guests.length) {
        return [createEmptyGuestRow()];
    }
    return guests.map((guest) => ({
        id: guest.id,
        firstName: guest.firstName,
        lastName: guest.lastName,
        phone: guest.phoneNumber ?? '',
    }));
}

/**
 * @returns {GuestFormRow}
 */
function createEmptyGuestRow() {
    return {
        id: `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        firstName: '',
        lastName: '',
        phone: '',
    };
}

/**
 * @param {GuestFormRow[]} rows
 */
function validateGuestRows(rows) {
    if (rows.length === 0) return false;
    return rows.every(
        (row) =>
            row.firstName.trim() &&
            row.lastName.trim() &&
            row.phone.trim(),
    );
}

/**
 * @param {{
 *   open: boolean,
 *   guests: MeetingGuest[],
 *   meetingTitle?: string,
 *   submitting?: boolean,
 *   submitError?: string | null,
 *   onClose: () => void,
 *   onSubmit: (guests: Array<{
 *     id?: string,
 *     firstName: string,
 *     lastName: string,
 *     phoneNumber: string,
 *   }>) => void | Promise<void>,
 * }} props
 */
export function MeetingGuestEditModal({
    open,
    guests,
    meetingTitle = '',
    submitting = false,
    submitError = null,
    onClose,
    onSubmit,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const [guestRows, setGuestRows] = useState(
        /** @type {GuestFormRow[]} */ ([createEmptyGuestRow()]),
    );
    const [formError, setFormError] = useState(
        /** @type {string | null} */ (null),
    );

    useEffect(() => {
        if (open) {
            setGuestRows(mapGuestsToRows(guests));
            setFormError(null);
        }
    }, [open, guests]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose, submitting]);

    if (!open) return null;

    function handleGuestFieldChange(rowId, field, value) {
        setFormError(null);
        setGuestRows((prev) =>
            prev.map((row) =>
                row.id === rowId ? { ...row, [field]: value } : row,
            ),
        );
    }

    function handleAddGuestRow() {
        setFormError(null);
        setGuestRows((prev) => [...prev, createEmptyGuestRow()]);
    }

    function handleRemoveGuestRow(rowId) {
        setFormError(null);
        setGuestRows((prev) =>
            prev.length <= 1
                ? prev
                : prev.filter((row) => row.id !== rowId),
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setFormError(null);

        if (!validateGuestRows(guestRows)) {
            setFormError(t('eventCalendarPage.guestFormValidationError'));
            return;
        }

        const mappedGuests = guestRows.map((row) => ({
            ...(row.id.startsWith('guest-') ? {} : { id: row.id }),
            firstName: row.firstName.trim(),
            lastName: row.lastName.trim(),
            phoneNumber: row.phone.trim(),
        }));

        try {
            await onSubmit(mappedGuests);
        } catch {
            // submitError parent'tan gelir
        }
    }

    const displayError = formError || submitError;

    return (
        <div
            className="app-modal-backdrop app-modal-backdrop--stacked"
            onClick={submitting ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal app-modal--wide event-calendar-guest-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <div className="min-w-0">
                        <h2 id={titleId} className="app-modal-title h6 mb-0">
                            {t('eventCalendarPage.editGuestsModalTitle')}
                        </h2>
                        {meetingTitle ? (
                            <p className="event-calendar-guest-modal-meeting small text-secondary mb-0 mt-1">
                                {meetingTitle}
                            </p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label={t('eventCalendarPage.detailClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>

                <form className="app-modal-body app-modal-body--scroll" onSubmit={handleSubmit}>
                    <p className="event-calendar-guest-modal-intro small text-secondary mb-3">
                        {t('eventCalendarPage.editGuestsIntro')}
                    </p>
                    <ul className="event-calendar-guest-list list-unstyled mb-0">
                        {guestRows.map((row, index) => (
                            <li
                                key={row.id}
                                className="event-calendar-guest-card"
                            >
                                <div className="event-calendar-guest-card-head d-flex align-items-center justify-content-between gap-2 mb-3">
                                    <h3 className="event-calendar-guest-card-title h6 mb-0">
                                        {t('eventCalendarPage.guestCardTitle', {
                                            index: index + 1,
                                        })}
                                    </h3>
                                    {guestRows.length > 1 ? (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                                            onClick={() =>
                                                handleRemoveGuestRow(row.id)
                                            }
                                            disabled={submitting}
                                            aria-label={t(
                                                'eventCalendarPage.removeGuestAria',
                                                { index: index + 1 },
                                            )}
                                        >
                                            <Trash2
                                                size={14}
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                            {t('eventCalendarPage.removeGuest')}
                                        </button>
                                    ) : null}
                                </div>
                                <div className="event-calendar-guest-modal-fields">
                                    <div className="mb-3">
                                        <label
                                            htmlFor={`${row.id}-first-name`}
                                            className="form-label event-calendar-detail-field-label"
                                        >
                                            {t(
                                                'eventCalendarPage.detailGuestFirstName',
                                            )}
                                        </label>
                                        <input
                                            id={`${row.id}-first-name`}
                                            type="text"
                                            className="form-control"
                                            value={row.firstName}
                                            onChange={(e) =>
                                                handleGuestFieldChange(
                                                    row.id,
                                                    'firstName',
                                                    e.target.value,
                                                )
                                            }
                                            autoComplete="given-name"
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label
                                            htmlFor={`${row.id}-last-name`}
                                            className="form-label event-calendar-detail-field-label"
                                        >
                                            {t(
                                                'eventCalendarPage.detailGuestLastName',
                                            )}
                                        </label>
                                        <input
                                            id={`${row.id}-last-name`}
                                            type="text"
                                            className="form-control"
                                            value={row.lastName}
                                            onChange={(e) =>
                                                handleGuestFieldChange(
                                                    row.id,
                                                    'lastName',
                                                    e.target.value,
                                                )
                                            }
                                            autoComplete="family-name"
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="mb-0">
                                        <label
                                            htmlFor={`${row.id}-phone`}
                                            className="form-label event-calendar-detail-field-label"
                                        >
                                            {t(
                                                'eventCalendarPage.detailGuestPhone',
                                            )}
                                        </label>
                                        <input
                                            id={`${row.id}-phone`}
                                            type="tel"
                                            className="form-control"
                                            value={row.phone}
                                            onChange={(e) =>
                                                handleGuestFieldChange(
                                                    row.id,
                                                    'phone',
                                                    e.target.value,
                                                )
                                            }
                                            autoComplete="tel"
                                            disabled={submitting}
                                        />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        className="btn btn-outline-primary d-inline-flex align-items-center gap-1 mt-3"
                        onClick={handleAddGuestRow}
                        disabled={submitting}
                    >
                        <Plus size={16} strokeWidth={2} aria-hidden />
                        {t('eventCalendarPage.addGuest')}
                    </button>

                    {displayError ? (
                        <p
                            className="app-modal-error small text-danger mb-0 mt-3"
                            role="alert"
                        >
                            {displayError}
                        </p>
                    ) : null}

                    <button
                        type="submit"
                        className="btn btn-primary w-100 event-calendar-detail-join-btn mt-3"
                        disabled={submitting}
                    >
                        {submitting
                            ? t('eventCalendarPage.updateGuestsSubmitting')
                            : t('eventCalendarPage.saveGuests')}
                    </button>
                </form>
            </div>
        </div>
    );
}
